/**
 * SettingItem
 *
 * Reusable sub-component for content settings rows.
 * Two variants: toggle (with ToggleSwitch) and tappable (with status text + chevron).
 * Styling follows SettingToggleItem.tsx patterns.
 */

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ToggleSwitch } from '@/presentation/components/common/controls/ToggleSwitch';
import { ForwardChevronIcon } from '@/presentation/components/common/ForwardChevronIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

export enum ContentSettingVariant {
  toggle = 'toggle',
  tappable = 'tappable',
}

interface ContentSettingItemBase {
  iconPath: string;
  label: string;
  description?: string;
  /**
   * `data-qa-id` for this settings row. Toggle rows additionally expose
   * `data-qa-value` (`on`/`off`) on the inner `ToggleSwitch`; tappable rows
   * carry the id on the row container so the qa-tester can click it.
   */
  qaId?: string;
}

interface ContentToggleSettingItem extends ContentSettingItemBase {
  variant: ContentSettingVariant.toggle;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

interface TappableContentSettingItem extends ContentSettingItemBase {
  variant: ContentSettingVariant.tappable;
  statusText?: string;
  onClick: () => void;
  disabled?: boolean;
}

type ContentSettingItemProps = ContentToggleSettingItem | TappableContentSettingItem;

export const ContentSettingItem = (props: ContentSettingItemProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const isDisabled = props.disabled ?? false;

  const handleClick = () => {
    if (props.variant === ContentSettingVariant.tappable && !isDisabled) {
      props.onClick();
    }
  };

  return (
    <div
      {...(props.qaId && props.variant === ContentSettingVariant.tappable
        ? qaAttr({ id: props.qaId })
        : {})}
      onClick={props.variant === ContentSettingVariant.tappable ? handleClick : undefined}
      style={{
        opacity: isDisabled ? 0.5 : 1,
        cursor: props.variant === ContentSettingVariant.tappable && !isDisabled ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: responsive.spacing.xs,
        }}
      >
        <AssetIcon
          path={props.iconPath}
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.disabled}
        />
        <span
          style={{
            flex: 1,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
          }}
        >
          {props.label}
        </span>
        {props.variant === ContentSettingVariant.toggle && (
          <ToggleSwitch
            value={props.value}
            onChange={props.onChange}
            disabled={isDisabled}
            qa={props.qaId ? { id: props.qaId, value: props.value ? 'on' : 'off' } : undefined}
          />
        )}
        {props.variant === ContentSettingVariant.tappable && (
          <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xxs }}>
            {props.statusText && (
              <span
                style={{
                  fontSize: responsive.font.sm,
                  color: theme.text.secondary,
                }}
              >
                {props.statusText}
              </span>
            )}
            <ForwardChevronIcon />
          </div>
        )}
      </div>
      {props.description && (
        <div
          style={{
            fontSize: responsive.font.sm,
            color: theme.text.secondary,
            fontWeight: responsive.fontWeight.medium,
            marginTop: responsive.spacing.sm,
          }}
        >
          {props.description}
        </div>
      )}
    </div>
  );
};
